import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';

// import { Product } from '../core/models/product';
import { ProductOption } from '../core/models/productOption';
import { ShippingOption } from '../core/models/shippingOption';
import { Cart, CartItem, CartFormItem, Order, OrderResponse } from '../core/models/cart';

import { environment } from '../../environments/environment';
import * as Raven from 'raven-js';


@Injectable()
export class CartService {

    private _localCart = new Cart();

    completedOrder: any;
    shippingOptions: ShippingOption[];
    selectedShippingOption: ShippingOption;

    donationAmount: BehaviorSubject<number> = new BehaviorSubject(0);
    donationAmount$ = this.donationAmount.asObservable();

    promoCode: BehaviorSubject<string> = new BehaviorSubject('');
    promoCode$ = this.promoCode.asObservable();

    discountAmount: BehaviorSubject<number> = new BehaviorSubject(0);
    discountAmount$ = this.discountAmount.asObservable();

    emptyCart: Cart = new Cart();
    itemsInCart: BehaviorSubject<number> = new BehaviorSubject(0);
    itemsInCart$ = this.itemsInCart.asObservable();

    cartTotal: BehaviorSubject<number> = new BehaviorSubject(0);
    cartTotal$ = this.cartTotal.asObservable();

    promoCodeDiscountActive = false;

    constructor(private http: HttpClient) {
        this.initCart();
    }

    private isLocalStorageEnabled() {
        try {
            var mod = '__storage_test__';
            localStorage.setItem(mod, mod);
            localStorage.removeItem(mod);
            return true;
        } catch (e) {
            return false;
        }
    }

    initCart() {
        if (this.isLocalStorageEnabled()) {
            if (!localStorage.getItem('cart')) {
                this.setCart(this.emptyCart);
            }
            this.updateCartTotals();
        }
    }

    getAllCartItems() {
        let cart = this.getCart();
        let cartItems: CartItem[] = [];

        // tslint:disable-next-line:forin
        for (const key in cart) {
            const value = cart[key];
            cartItems.push(new CartItem(value.product, value.productTags, value.name, +value.quantity, value.discount));
        }
        return Promise.resolve(cartItems);
    }

    getCartItemByProductCode(productCode: string) {
        let cart = this.getCart();
        return Promise.resolve(cart[productCode]);
    };

    updateCartTotals() {
        var itemCount = 0;
        var cartTotal = 0;
        var promoCode = '';
        let cart = this.getCart();
        if (localStorage.getItem('promo-code')) {

            promoCode = localStorage.getItem('promo-code');
            this.applyPromoCodeDiscount(promoCode);
        }
        this.recalculateDiscounts(cart);


        // tslint:disable-next-line:forin
        for (const key in cart) {
            const value = <CartItem>cart[key];
            const item = new CartItem(value.product, value.productTags, value.name, +value.quantity, value.discount);
            itemCount += item.quantity;
            cartTotal += (item.cost - item.discount);
        }

        this.itemsInCart.next(itemCount);
        this.cartTotal.next(cartTotal);
    }

    addProductToCart(product: ProductOption, productTags: string[], name: string, quantity: number): void {
        let cart = this.getCart();
        const discount = 0.0;
        if (cart[product.sku] !== undefined) {
            const cartItem = cart[product.sku];
            cartItem.quantity += +quantity;
            cart[product.sku] = cartItem;
        } else {
            cart[product.sku] = new CartItem(product, productTags, name, +quantity, discount);
        }

        this.setCart(cart);
        this.updateCartTotals();
    }

    removeProductFromCart(productCode: string): void {
        const cart = this.getCart();
        if (cart[productCode] !== undefined) {
            delete cart[productCode];
            this.setCart(cart);
            this.updateCartTotals();
        }
    }

    UpdateProductQuantities(dirtyItems: Array<CartFormItem>) {
        const cart = this.getCart();

        for (var i = 0; i < dirtyItems.length; i++) {
            if (cart[dirtyItems[i].productCode] != undefined) {
                cart[dirtyItems[i].productCode].quantity = +dirtyItems[i].quantity;
            }
        }
        this.setCart(cart);
        this.updateCartTotals();

        return this.getAllCartItems();
    }

    private getCart(): Cart {
        let cartAsString = '{}';
        let cart = {};
        if (this.isLocalStorageEnabled()) {
            try {
                cartAsString = localStorage.getItem('cart');
                cart = JSON.parse(cartAsString);
            } catch (err) {
                cart = this._localCart;
                throw err;
            }
        } else {
            cart = this._localCart;
        }
        return cart;
    }

    private setCart(cart: Cart): void {
        if (this.isLocalStorageEnabled()) {
            try {
                localStorage.setItem('cart', JSON.stringify(cart));
            } catch (err) {
                // alert('Your web browser does not support storing settings locally. In Safari, the most common ' +
                // 'cause of this is using "Private Browsing Mode". Some settings may not save or some features 
                // may not work properly for you.');

                if (environment.production) {
                    Raven.captureMessage('User alerted to issue with Private Browsing Mode on Safari. Ex:' + err.toString())
                }
                throw err;
            }
        }
    }

    clearTheCart() {
        this.completedOrder = {};
        localStorage.removeItem('cart');
        localStorage.removeItem('promo-code');
        this.initCart();
    }

    recalculateDiscounts(cart: Cart) {
        if (cart !== null) {
            //const cyberMondayDiscount = this.getCyberMondayDiscount(cart);
            const israelCourseBookDiscount = this.getIsraelCourseBookDiscount(cart);
            // const shirtDiscount = this.getShirtDiscount(cart);
            let promoCodeDiscount = 0.0;
            let totalDiscount = 0.0;

            // if (this.promoCodeDiscountActive) {
            //     promoCodeDiscount = this.getPromoCodeDiscount(cart);
            // }

            totalDiscount = promoCodeDiscount;

            // // take the greater discount only
            // if (promoCodeDiscount >= cyberMondayDiscount) {
            //     totalDiscount = promoCodeDiscount;
            //     //this.applyShirtDiscount(cart);
            // } else {
            //     totalDiscount = cyberMondayDiscount;
            //     this.applyCyberMondayDiscount(cart);
            // }
            
            // Always apply
            totalDiscount += israelCourseBookDiscount;
            this.applyIsraelCourseBookDiscount(cart);


            this.discountAmount.next(totalDiscount);
        }
    }

    // applyShirtDiscount(cart: Cart) {
    //     let totalDiscount = 0.0;
    //     const shirts = [];
    //     let numberOfShirts = 0;
    //     const keys = Object.keys(cart);
    //     let orderDiscount = 0.0;

    //     // tslint:disable-next-line:forin
    //     for (const index in keys) {
    //         const product = cart[keys[index]];

    //         if (product !== undefined) {
    //             product.discount = 0;
    //             if (product.productTags.includes('Shirts')) {
    //                 shirts.push(product);
    //                 numberOfShirts += product.quantity;
    //             };
    //         }
    //     };


    //     totalDiscount = ((numberOfShirts - (numberOfShirts % 2)) / 2) * 5;
    //     // tslint:disable-next-line:forin
    //     for (const index in shirts) {
    //         const shirt = shirts[index];

    //         // how much of totalDiscount should this get?
    //         const lineItemDiscount = shirt.quantity * 2.5;
    //         if (lineItemDiscount < totalDiscount) {
    //             shirt.discount += lineItemDiscount;
    //             orderDiscount += lineItemDiscount;
    //             totalDiscount -= lineItemDiscount
    //         } else {
    //             shirt.discount += totalDiscount;
    //             orderDiscount += totalDiscount;
    //             totalDiscount -= totalDiscount;
    //         }
    //     }

    //     this.setCart(cart);

    //     return orderDiscount;
    // }





    // getShirtDiscount(cart: Cart) {
    //     let totalDiscount = 0.0;
    //     const shirts = [];
    //     let numberOfShirts = 0;
    //     const keys = Object.keys(cart);
    //     let orderDiscount = 0.0;

    //     // tslint:disable-next-line:forin
    //     for (const index in keys) {
    //         const product = cart[keys[index]];

    //         if (product !== undefined && product.productTags.includes('Shirts')) {
    //             // product.discount = 0;
    //             shirts.push(product);
    //             numberOfShirts += product.quantity;
    //         }
    //     };



    //     totalDiscount = ((numberOfShirts - (numberOfShirts % 2)) / 2) * 5;
    //     // tslint:disable-next-line:forin
    //     for (const index in shirts) {
    //         const shirt = shirts[index];

    //         // how much of totalDiscount should this get?
    //         const lineItemDiscount = shirt.quantity * 2.5;
    //         if (lineItemDiscount < totalDiscount) {
    //             // shirt.discount += lineItemDiscount;
    //             orderDiscount += lineItemDiscount;
    //             totalDiscount -= lineItemDiscount;
    //         } else {
    //             // shirt.discount += totalDiscount;
    //             orderDiscount += totalDiscount;
    //             totalDiscount -= totalDiscount;
    //         }
    //     }

    //     return orderDiscount;
    // }


    applyIsraelCourseBookDiscount(cart: Cart) {

        let totalDiscount = 0.0;
        let unitDiscount = 0.0;
        const books = [];
        let numberOfBooks = 0;
        const keys = Object.keys(cart);
        let bookDiscount = 0.0;

        // tslint:disable-next-line:forin
        for (const index in keys) {
            const product = cart[keys[index]];

            if (product !== undefined && product.product.sku.includes('ICBOOKKIT')) {
                product.discount = 0;
                books.push(product);
                numberOfBooks += product.quantity;
            }
        };


        var promoCode = '';
        if (localStorage.getItem('promo-code')) {
            promoCode = localStorage.getItem('promo-code').toUpperCase();
        }

        if (promoCode.toUpperCase() === 'HIGHSCHOOL') {
            unitDiscount = 8.0;
            totalDiscount = numberOfBooks * unitDiscount;
        } else {
            if (numberOfBooks >= 10) {
                unitDiscount = 5.0;
            } else if (numberOfBooks >= 2) {
                unitDiscount = 2.0;
            }
            totalDiscount = numberOfBooks * unitDiscount;
        } 
        

        for (const index in books) {
            const book = books[index];

            // how much of totalDiscount should this get?
            const lineItemDiscount = book.quantity * unitDiscount;
            if (lineItemDiscount < totalDiscount) {
                book.discount += lineItemDiscount;
                bookDiscount += lineItemDiscount;
                totalDiscount -= lineItemDiscount;
            } else {
                book.discount += totalDiscount;
                bookDiscount += totalDiscount;
                totalDiscount -= totalDiscount;
            }
        }


        this.setCart(cart);
        return bookDiscount;
    }

    getIsraelCourseBookDiscount(cart: Cart) {

        let totalDiscount = 0.0;
        let unitDiscount = 0.0;
        const books = [];
        let numberOfBooks = 0;
        const keys = Object.keys(cart);
        let bookDiscount = 0.0;

        // tslint:disable-next-line:forin
        for (const index in keys) {
            const product = cart[keys[index]];

            if (product !== undefined && product.product.sku.includes('ICBOOKKIT')) {
                books.push(product);
                numberOfBooks += product.quantity;
            }
        };
        var promoCode = '';
        if (localStorage.getItem('promo-code')) {
            promoCode = localStorage.getItem('promo-code').toUpperCase();
        }

        if (promoCode.toUpperCase() === 'HIGHSCHOOL') {
                unitDiscount = 8.0;
                totalDiscount = numberOfBooks * unitDiscount;
        } else {
            if (numberOfBooks >= 10) {
                unitDiscount = 5.0;
            } else if (numberOfBooks >= 2) {
                unitDiscount = 2.0;
            }
            totalDiscount = numberOfBooks * unitDiscount;
        } 

        

        for (const index in books) {
            const book = books[index];

            // how much of totalDiscount should this get?
            const lineItemDiscount = book.quantity * unitDiscount;
            if (lineItemDiscount < totalDiscount) {
                bookDiscount += lineItemDiscount;
                totalDiscount -= lineItemDiscount;
            } else {
                bookDiscount += totalDiscount;
                totalDiscount -= totalDiscount;
            }
        }

        return bookDiscount;
    }

   

    // applyCyberMondayDiscount(cart: Cart) {
    //     const keys = Object.keys(cart);
    //     let totalDis = 0;
    //     try {
    //         var checkTime = new Date();
    //         let percentageOff = 0;

    //         // **NOTE: JS Date() month objects are zero indexed **//
    //         const Start30Off = new Date(2019, 11, 2, 6);
    //         const End30Off = new Date(2019, 11, 2, 11);

    //         const Start20Off = new Date(2019, 11, 2, 11);
    //         const End20Off = new Date(2019, 11, 2, 13);

    //         const Start10Off = new Date(2019, 11, 2, 13);
    //         const End10Off = new Date(2019, 11, 2, 17);



    //         if (Start30Off <= checkTime && checkTime < End30Off)
    //             percentageOff = .30;
    //         else if (Start20Off <= checkTime && checkTime < End20Off)
    //             percentageOff = .20;
    //         else if (Start10Off <= checkTime && checkTime < End10Off)
    //             percentageOff = .10;

    //         if (percentageOff > 0) {


    //             // tslint:disable-next-line:forin
    //             for (const index in keys) {
    //                 const lineItem = cart[keys[index]];
    //                 if (lineItem !== undefined && !lineItem.productTags.includes('Megemeria') && !lineItem.product.sku.includes('ICBOOKKIT')) {
    //                     let newDiscount = (lineItem.product.price * lineItem.quantity) * percentageOff;

    //                     lineItem.discount = newDiscount;
    //                     totalDis += lineItem.discount;
    //                 }
    //             };

    //             this.setCart(cart);
    //         }

    //         return totalDis;
    //     }
    //     catch (e) {
    //         throw e;
    //     }
    // }

    // getCyberMondayDiscount(cart: Cart) {
    //     const keys = Object.keys(cart);
    //     let totalDis = 0;

    //     try {
    //         var checkTime = new Date();
    //         let percentageOff = 0;

    //         // **NOTE: JS Date() month objects are zero indexed **//
    //         const Start30Off = new Date(2019, 11, 2, 6);
    //         const End30Off = new Date(2019, 11, 2, 11);

    //         const Start20Off = new Date(2019, 11, 2, 11);
    //         const End20Off = new Date(2019, 11, 2, 13);

    //         const Start10Off = new Date(2019, 11, 2, 13);
    //         const End10Off = new Date(2019, 11, 2, 17);


    //         if (Start30Off <= checkTime && checkTime < End30Off)
    //             percentageOff = .30;
    //         else if (Start20Off <= checkTime && checkTime < End20Off)
    //             percentageOff = .20;
    //         else if (Start10Off <= checkTime && checkTime < End10Off)
    //             percentageOff = .10;

    //         if (percentageOff > 0) {
    //             // tslint:disable-next-line:forin
    //             for (const index in keys) {
    //                 const lineItem = cart[keys[index]];
    //                 if (lineItem !== undefined && !lineItem.productTags.includes('Megemeria')) {
    //                     let newDiscount = (lineItem.product.price * lineItem.quantity) * percentageOff;

    //                     totalDis += newDiscount;
    //                 }
    //             };
    //         }

    //         return totalDis;
    //     }
    //     catch (e) {
    //         throw e;
    //     }
    // }

    setSelectedShippingOption(event, option: ShippingOption) {
        this.selectedShippingOption = option;

        return this.selectedShippingOption;
    }

    validatePromoCode(code: string): boolean {
        const today = new Date();
        // const promo1Code = 'THANKSGIVING';
        // const promo2Code = 'CHRISTMAS';
        const promo3Code = 'HIGHSCHOOL';
        


        // // Promo 1
        // const StartPromo1Discount = new Date(2018, 10, 19, 0); // zero indexed months
        // const EndPromo1Discount = new Date(2018, 10, 26, 0);

        // // Promo 2
        // const StartPromo2Discount = new Date(2018, 10, 26, 0);
        // const EndPromo2Discount = new Date(2018, 12, 21, 0);

        // if (StartPromo1Discount <= today && today < EndPromo1Discount) {
        //     return code.toUpperCase() === promo1Code;
        // }

        // if (StartPromo2Discount <= today && today < EndPromo2Discount) {
        //     return code.toUpperCase() === promo2Code;
        // }

        return code.toUpperCase() === promo3Code;

        // return false;
    }

    applyPromoCodeDiscount(code: string): string {

        this.promoCode.next(code.toUpperCase());
        this.promoCodeDiscountActive = true;
        localStorage.setItem('promo-code', code);

        let cart = this.getCart();
        this.recalculateDiscounts(cart);
        //cart.discount = discountTotal;
        //Saving cart
        this.setCart(cart);
        


        return 'Promo code discount applied';
        // return '10% discount applied';
    }

    removePromoCodeDiscount(): string {
        this.promoCode.next('');
        this.promoCodeDiscountActive = false;
        localStorage.removeItem('promo-code');
        let cart = this.getCart();
        this.clearDiscounts(cart);
        this.recalculateDiscounts(cart);
        this.setCart(cart);


        return '';
    }

    // getPromoCodeDiscount(cart: Cart) {
    //     const keys = Object.keys(cart);
    //     let totalDis = 0.0;

    //     // tslint:disable-next-line:forin
    //     for (const index in keys) {
    //         const lineItem = cart[keys[index]];
    //         lineItem.discount = 0;
    //         if (lineItem !== undefined && !lineItem.productTags.includes('Megemeria') && !lineItem.product.sku.includes('ICBOOKKIT')) {
    //             let newDiscount = (lineItem.product.price * lineItem.quantity) * .1;

    //             totalDis += lineItem.discount;
    //             totalDis += newDiscount;

    //             lineItem.discount = newDiscount;
    //         }
    //     };

    //     this.setCart(cart);

    //     return totalDis;
    // }

    setDonationAmount(event, amount: number) {
        this.donationAmount = new BehaviorSubject(amount);
    }

    clearDiscounts(cart: Cart) {
        const keys = Object.keys(cart);
        for (const index in keys) {
            const lineItem = cart[keys[index]];
            lineItem.discount = 0;
        }
    }

    getDonationAmount() {
        return this.donationAmount;
    }


    setPromoCode(code: string) {
        this.promoCode = new BehaviorSubject(code);
    }

    getPromoCode() {
        return this.promoCode;
    }

    getShippingOptions(order: any, cartItems: CartItem[]) {
        let totalWeight = 0.0;
        cartItems.forEach(item => {
            totalWeight += item.product.weight * item.quantity;
        });

        const postalCode = order.shippingAddress.postalCode;
        const countryCode = order.shippingAddress.country;
        const streetFirst = order.shippingAddress.addressLine1;
        const streetSecond = order.shippingAddress.addressLine2;
        const city = order.shippingAddress.city;
        const stateOrProvinceCode = order.shippingAddress.state;

        const body = {
            postalCode, countryCode, streetFirst,
            streetSecond, city, stateOrProvinceCode, totalWeight
        };
        return this.http
            .post<ShippingOption[]>(environment.shippingApiBaseUrl + '/fedex/options', body)
            .map(res => this.shippingOptions = res);
    }

    processOrder(order: any, cartItems: CartItem[]): Observable<OrderResponse> {

        if (order.billingIsTheSameAsShipping) {
            Object.assign(order.billingAddress, order.shippingAddress);
        }

        const orderRequest: Order = {
            shippingAddress: order.shippingAddress,
            billingAddress: order.billingAddress,
            creditCard: order.creditCard,
            items: cartItems,
            shippingMethod: this.selectedShippingOption.ServiceType,
            shippingTotal: this.selectedShippingOption.Price.Amount,
            donationTotal: this.donationAmount.value,
            discountTotal: this.discountAmount.value
        };

        const body = JSON.stringify(orderRequest);
        return this.http.post<OrderResponse>(environment.ecommerceApiBaseUrl + '/transactions', body);
    }
}



// WEBPACK FOOTER //
// ./src/app/cart/cart.service.ts